import {getTestProps} from '@benchling/js-test-utils';
import {css} from 'aphrodite';
import React, {useCallback, useState} from 'react';

import {agentProgressStyles} from 'llm/chat-library/messages/ChatProgress';
import {ToolActivityItem} from 'llm/chat-library/messages/ToolActivityItem';
import {ToolDetail} from 'llm/deep-research/tool-details/ToolDetail';
import {
  getToolIcon,
  DeepResearchProgressToolCall,
  DeepResearchProgressData,
} from 'llm/deep-research/tool-details/types';
import {MCPToolMappingProvider} from 'llm/mcp-client-library/MCPToolMappingContext';
import * as ModalContainer from 'ui-library/ModalContainer';
import {ExpandableModal} from 'ui-library/modals/ExpandableModal';
import {ModalButton} from 'ui-library/modals/Modal';

const ACTIVITY_MODAL_KEY = 'ACTIVITY_MODAL_KEY';
const TEST_COMPONENT = 'DeepResearchProgress';

export interface ActivityModalControls {
  openActivityModal: (activityData: DeepResearchProgressData, selectedToolId?: string) => void;
}

export function useActivityModalControls(): ActivityModalControls {
  const openActivityModal = useCallback((activityData: DeepResearchProgressData, selectedToolId?: string) => {
    ModalContainer.open(
      ACTIVITY_MODAL_KEY,
      <ActivityModal
        activityData={activityData}
        initialSelectedToolId={selectedToolId}
        closeModal={() => {
          ModalContainer.close(ACTIVITY_MODAL_KEY);
        }}
      />
    );
  }, []);

  return {
    openActivityModal,
  };
}

export interface ActivityModalProps {
  activityData: DeepResearchProgressData;
  closeModal: () => void;
  initialSelectedToolId?: string;
}

function ActivityList({
  activityData,
  onToolClick,
}: {
  activityData: DeepResearchProgressData;
  onToolClick: (toolCall: DeepResearchProgressToolCall) => void;
}): JSX.Element {
  const {historyThread: toolCalls} = activityData;

  return (
    <div
      className={css(agentProgressStyles.activityContent)}
      {...getTestProps({
        component: TEST_COMPONENT,
        element: 'content',
      })}
    >
      {toolCalls.map((toolCall, index) => (
        <ToolActivityItem
          key={toolCall.toolId || index}
          icon={getToolIcon(toolCall.tool)}
          displayName={toolCall.displayName}
          onClick={() => {
            onToolClick(toolCall);
          }}
          testComponent={TEST_COMPONENT}
          testKey={`${index}`}
        />
      ))}
    </div>
  );
}

/**
 * Modal for displaying detailed progress activity for completed deep research.
 * Shows the full activity thread.
 */
export function ActivityModal({
  activityData,
  closeModal,
  initialSelectedToolId,
}: ActivityModalProps): JSX.Element {
  const {historyThread: toolCalls} = activityData;

  // Find initial tool if ID provided
  const initialTool = initialSelectedToolId
    ? toolCalls.find((tool) => tool.toolId === initialSelectedToolId) || null
    : null;

  const [selectedTool, setSelectedTool] = useState<DeepResearchProgressToolCall | null>(initialTool);

  const handleToolClick = useCallback((toolCall: DeepResearchProgressToolCall) => {
    setSelectedTool(toolCall);
  }, []);

  const handleBackToActivity = useCallback(() => {
    setSelectedTool(null);
  }, []);

  const buttons: Array<ModalButton> = selectedTool
    ? [
        {
          text: 'Back',
          onClick: handleBackToActivity,
        },
        {
          text: 'Close',
          onClick: closeModal,
        },
      ]
    : [
        {
          text: 'Close',
          onClick: closeModal,
        },
      ];

  const title = selectedTool ? 'Tool Details' : 'Activity';

  return (
    <MCPToolMappingProvider>
      <ExpandableModal
        title={title}
        onRequestClose={closeModal}
        buttons={buttons}
        closeableOnEscapeKey={true}
      >
        {selectedTool ? (
          <ToolDetail toolCall={selectedTool} />
        ) : (
          <ActivityList activityData={activityData} onToolClick={handleToolClick} />
        )}
      </ExpandableModal>
    </MCPToolMappingProvider>
  );
}
